import clsx from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import { IconButton } from '#common/components/IconButton.tsx';
import { useAudio } from './AudioContext';
import { useRecordingContext } from './RecordingContext';

interface Props {
    audio: Blob | string;
    takeId: string;
    stopTitle: string;
    playTitle: string;
    buttonClassName?: string | ((isPlayingAudio: boolean) => string);
    disabledButtonClassName?: string;
    /** Overrides the default text colour of the enabled button (avoids conflicting utilities). */
    colorClassName?: string;
}

export function PlayButton(
    { audio, takeId, stopTitle, playTitle, disabledButtonClassName, buttonClassName, colorClassName }: Props,
) {
    const { playAudio, stopAudio, isPlayingAudio } = useAudio();
    const { fetchAudioForTake } = useRecordingContext();
    const [ isFetchingAudio, setIsFetchingAudio ] = useState(false);
    const [ fetchError, setFetchError ] = useState<string | null>(null);

    const hasAudio = useMemo(() => {
        if (typeof audio === 'string') {
            return audio.length > 0;
        }
        return audio.size > 0;
    }, [ audio ]);

    // Check if this is a restored take that needs fetching
    // A restored take has: empty audio BUT valid takeId
    const isRestoredTake = useMemo(() => {
        return !hasAudio && takeId && takeId.length > 0;
    }, [ hasAudio, takeId ]);

    const isDisabled = !hasAudio && !isRestoredTake;

    const icon = useMemo(() => (isPlayingAudio ? 'heroicons--stop-solid' : 'heroicons--play-solid'), [
        isPlayingAudio,
    ]);
    const hoverIcon = useMemo(() => (isPlayingAudio ? 'heroicons--stop-solid' : 'heroicons--play-solid'), [
        isPlayingAudio,
    ]);
    const className = useMemo(() => {
        if (!buttonClassName) {
            return undefined;
        }
        if (typeof buttonClassName === 'string') {
            return buttonClassName;
        }
        return buttonClassName(isPlayingAudio);
    }, [ buttonClassName, isPlayingAudio ]);

    const onClick = useCallback(async () => {
        setFetchError(null);
        if (isPlayingAudio) {
            stopAudio();
            return;
        }
        try {
            if (isRestoredTake) {
                console.log('🔄 Fetching audio for restored take:', takeId);
                setIsFetchingAudio(true);
                const audioUrl = await fetchAudioForTake(takeId);
                setIsFetchingAudio(false);

                console.log('▶️ Auto-playing fetched audio:', audioUrl);
                playAudio(audioUrl);
            } else if (hasAudio) {
                console.log('▶️ Playing audio directly:', audio);
                playAudio(audio);
            }
        } catch (error) {
            setIsFetchingAudio(false);
            setFetchError('Failed to load audio. File may have expired.');
            console.error('Error fetching audio:', error);
        }
    }, [ isPlayingAudio, stopAudio, isRestoredTake, hasAudio, fetchAudioForTake, takeId, playAudio, audio ]);

    if (fetchError) {
        return (
            <div className='text-red-500 text-sm' title={fetchError}>
                <IconButton
                    icon='heroicons--exclamation-triangle'
                    className={clsx(
                        'text-2xl items-center flex cursor-not-allowed text-red-500',
                        disabledButtonClassName
                    )}
                />
            </div>
        );
    }

    return !isDisabled
        ? (
            <IconButton
                icon={icon}
                hoverIcon={hoverIcon}
                onClick={onClick}
                title={isFetchingAudio
                    ? 'Loading audio...'
                    : isPlayingAudio
                    ? stopTitle
                    : isRestoredTake
                    ? 'Click to load audio'
                    : playTitle}
                className={clsx(
                    'text-2xl items-center flex cursor-pointer',
                    colorClassName ?? 'text-crispybluedark-600',
                    isFetchingAudio && 'animate-spin',
                    className
                )}
                disabled={isFetchingAudio}
            />
        )
        : (
            <IconButton
                icon={icon}
                className={clsx(
                    'text-2xl items-center flex cursor-not-allowed text-slate-500',
                    disabledButtonClassName
                )}
            />
        );
}
